import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { auth } from '../firebase/config.ts';
import {
  ensureTeamMissionsDocumentSeed,
  subscribeTeamMissions,
  writeTeamMissions,
} from '../services/missionsTeamService.ts';
import type { MissionRow, MissionsCatalog, MissionUpdate } from './missionTypes.ts';
import { MondayMissionDropdown } from './MondayMissionDropdown.tsx';
import type { MondayField } from './MondayMissionDropdown.tsx';
import {
  loadCatalog,
  saveCatalog,
  loadRows,
  saveRows,
  emptyRow,
  newUpdateId,
  reconcileRows,
} from './missionStorage.ts';
import './MissionsBoard.css';

type DropdownOpen = { rowId: string; field: MondayField };

function formatRelativeTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

type UpdatesAnchoredProps = {
  row: MissionRow | null;
  anchorRect: DOMRect | null;
  onClose: () => void;
  onPost: (missionId: string, body: string) => void;
};

const UpdatesAnchoredPopover: React.FC<UpdatesAnchoredProps> = ({ row, anchorRect, onClose, onPost }) => {
  const [draft, setDraft] = useState('');

  const sorted = useMemo(() => {
    if (!row?.updates?.length) return [];
    return [...row.updates].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [row]);

  useEffect(() => {
    if (!row) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', esc);
    /* Reposition drift: close on scroll (capture) */
    window.addEventListener('scroll', onClose, true);
    return () => {
      window.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [row, onClose]);

  if (!row || !anchorRect || typeof document === 'undefined') return null;

  const submit = () => {
    const t = draft.trim();
    if (!t) return;
    onPost(row.id, t);
    setDraft('');
  };

  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 600;
  const panelW = Math.min(440, vw - 16);
  let left = anchorRect.left;
  if (left + panelW > vw - 8) left = Math.max(8, vw - panelW - 8);
  if (left < 8) left = 8;
  const top = anchorRect.bottom + 8;
  const maxBodyH = Math.min(380, Math.max(160, vh - top - 24));

  return createPortal(
    <div className="missions-dd-theme">
      <div className="missions-updates-backdrop" role="presentation" onMouseDown={onClose} />
      <div
        className="missions-updates-popover"
        role="dialog"
        aria-labelledby="updates-pop-title"
        style={{ top, left, width: panelW, maxHeight: vh - 16 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="missions-updates-popover-header">
          <h2 id="updates-pop-title">Updates</h2>
          <button type="button" className="missions-updates-popover-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <p className="missions-updates-popover-mission">{row.mission.trim() || 'Untitled mission'}</p>
        <div className="missions-updates-popover-list" style={{ maxHeight: maxBodyH }}>
          {sorted.length === 0 ? (
            <p className="missions-updates-empty">No updates yet — start the thread.</p>
          ) : (
            sorted.map((u) => {
              const who =
                [u.authorDisplayName, u.authorEmail].filter(Boolean).join(' · ') || 'Team';
              return (
                <div key={u.id} className="missions-update-item">
                  <div className="missions-update-meta">
                    {who} · {formatRelativeTime(u.createdAt)}
                  </div>
                  <div className="missions-update-body">{u.body}</div>
                </div>
              );
            })
          )}
        </div>
        <div className="missions-updates-popover-compose">
          <textarea
            className="missions-input missions-input--compose"
            rows={3}
            placeholder="Write an update…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button type="button" className="missions-btn missions-btn--accent missions-updates-post-btn" onClick={submit}>
            Post update
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

type DetailsExpandProps = {
  row: MissionRow | null;
  onClose: () => void;
  onSave: (details: string) => void;
};

type RemoveMissionModalProps = {
  row: MissionRow | null;
  variant: 'active' | 'archived';
  onClose: () => void;
  onArchive: () => void;
  onDeletePermanent: () => void;
};

const RemoveMissionModal: React.FC<RemoveMissionModalProps> = ({
  row,
  variant,
  onClose,
  onArchive,
  onDeletePermanent,
}) => {
  if (!row) return null;
  const label = row.mission.trim() || 'Untitled mission';

  return (
    <div
      className="missions-modal-overlay missions-confirm-overlay"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="missions-modal missions-modal--confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="missions-remove-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="missions-modal-header">
          <h2 id="missions-remove-title">{variant === 'active' ? 'Remove mission?' : 'Delete permanently?'}</h2>
          <button type="button" className="missions-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <p className="missions-modal-sub missions-confirm-body">
          {variant === 'active'
            ? `Choose what happens to “${label}”. Archive hides it from the main board (open Archived to restore). Delete removes it for everyone.`
            : `Remove “${label}” permanently? This cannot be undone.`}
        </p>
        <div className="missions-confirm-actions">
          <button type="button" className="missions-btn missions-btn--ghost-lg" onClick={onClose}>
            Cancel
          </button>
          {variant === 'active' && (
            <button
              type="button"
              className="missions-btn missions-btn--soft"
              onClick={() => {
                onArchive();
                onClose();
              }}
            >
              Move to archive
            </button>
          )}
          <button
            type="button"
            className="missions-btn missions-btn--danger"
            onClick={() => {
              onDeletePermanent();
              onClose();
            }}
          >
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailsExpandModal: React.FC<DetailsExpandProps> = ({ row, onClose, onSave }) => {
  const [text, setText] = useState('');
  useEffect(() => {
    if (row) setText(row.details);
  }, [row]);

  if (!row) return null;

  return (
    <div className="missions-modal-overlay" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="missions-modal missions-modal--details" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="missions-modal-header">
          <h2>Details</h2>
          <button type="button" className="missions-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <textarea
          className="missions-input missions-details-full"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Notes"
          rows={12}
          aria-label="Details full text"
        />
        <div className="missions-details-actions">
          <button type="button" className="missions-btn missions-btn--ghost-lg" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="missions-btn missions-btn--accent"
            onClick={() => {
              onSave(text);
              onClose();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export const MissionsBoard: React.FC = () => {
  const [catalog, setCatalog] = useState<MissionsCatalog>(() => loadCatalog());
  const [rows, setRows] = useState<MissionRow[]>(() => loadRows(loadCatalog()));
  const [syncStatus, setSyncStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [syncError, setSyncError] = useState<string | null>(null);
  const skipSnapshotPersistRef = useRef(false);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [updatesUi, setUpdatesUi] = useState<{ rowId: string; rect: DOMRect } | null>(null);
  const [detailsRowId, setDetailsRowId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<DropdownOpen | null>(null);
  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active');
  const [pendingRemove, setPendingRemove] = useState<{ id: string; context: 'active' | 'archived' } | null>(null);

  const activeRows = useMemo(() => rows.filter((r) => !r.archived), [rows]);
  const archivedRows = useMemo(() => rows.filter((r) => r.archived), [rows]);
  const displayRows = viewMode === 'active' ? activeRows : archivedRows;

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      setSyncStatus('loading');
      setSyncError(null);
      try {
        await ensureTeamMissionsDocumentSeed();
        if (cancelled) return;
        unsub = subscribeTeamMissions(
          (payload) => {
            if (cancelled) return;
            skipSnapshotPersistRef.current = true;
            setCatalog(payload.catalog);
            setRows(payload.rows);
            setSyncStatus('ready');
            setSyncError(null);
          },
          (err) => {
            if (cancelled) return;
            setSyncError(err.message);
            setSyncStatus('error');
          }
        );
      } catch (e) {
        if (cancelled) return;
        setSyncError(e instanceof Error ? e.message : 'Could not connect to team missions');
        setSyncStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, []);

  useEffect(() => {
    saveCatalog(catalog);
    saveRows(rows);

    if (skipSnapshotPersistRef.current) {
      skipSnapshotPersistRef.current = false;
      return;
    }

    if (syncStatus !== 'ready') return;

    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null;
      void writeTeamMissions(catalog, rows).catch((e) => {
        setSyncError(e instanceof Error ? e.message : 'Could not save to team');
      });
    }, 850);

    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [catalog, rows, syncStatus]);

  useEffect(() => {
    if (viewMode === 'archived' && archivedRows.length === 0) setViewMode('active');
  }, [viewMode, archivedRows.length]);

  const updateRow = useCallback((id: string, patch: Partial<MissionRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const postUpdate = useCallback((missionId: string, body: string) => {
    const user = auth.currentUser;
    const u: MissionUpdate = {
      id: newUpdateId(),
      body,
      createdAt: new Date().toISOString(),
      authorEmail: user?.email ?? undefined,
      authorDisplayName: user?.displayName ?? undefined,
    };
    setRows((prev) =>
      prev.map((r) => (r.id === missionId ? { ...r, updates: [...r.updates, u] } : r))
    );
  }, []);

  const addMission = useCallback(() => {
    setViewMode('active');
    setRows((prev) => [...prev, emptyRow(catalog)]);
  }, [catalog]);

  const deleteMissionPermanent = useCallback(
    (id: string) => {
      setRows((prev) => {
        const next = prev.filter((r) => r.id !== id);
        if (next.length === 0) return [emptyRow(catalog)];
        return next;
      });
    },
    [catalog]
  );

  const archiveMission = useCallback((id: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, archived: true } : r)));
  }, []);

  const restoreMission = useCallback((id: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, archived: false } : r)));
  }, []);

  const pendingRemoveRow = useMemo(
    () => (pendingRemove ? rows.find((r) => r.id === pendingRemove.id) ?? null : null),
    [pendingRemove, rows]
  );

  const applyCatalogFromInline = useCallback((next: MissionsCatalog) => {
    saveCatalog(next);
    setCatalog(next);
    setRows((prev) => reconcileRows(prev, next));
  }, []);

  const updatesRow = useMemo(
    () => (updatesUi ? rows.find((r) => r.id === updatesUi.rowId) ?? null : null),
    [rows, updatesUi]
  );
  const detailsRow = useMemo(
    () => (detailsRowId ? rows.find((r) => r.id === detailsRowId) ?? null : null),
    [rows, detailsRowId]
  );

  return (
    <div className="missions-board">
      <div className="missions-toolbar">
        <div className="missions-toolbar-actions">
          <button type="button" className="missions-btn missions-btn--accent" onClick={addMission}>
            + New mission
          </button>
          {archivedRows.length > 0 && (
            <div className="missions-view-toggle" role="group" aria-label="Board view">
              <button
                type="button"
                className={`missions-view-toggle-btn ${viewMode === 'active' ? 'missions-view-toggle-btn--on' : ''}`}
                onClick={() => setViewMode('active')}
              >
                Active ({activeRows.length})
              </button>
              <button
                type="button"
                className={`missions-view-toggle-btn ${viewMode === 'archived' ? 'missions-view-toggle-btn--on' : ''}`}
                onClick={() => setViewMode('archived')}
              >
                Archived ({archivedRows.length})
              </button>
            </div>
          )}
        </div>
        <span className="missions-toolbar-hint">
          {syncStatus === 'loading' && <>Loading shared board… · </>}
          {syncStatus === 'ready' && <>Synced for all admins · </>}
          {syncStatus === 'error' && syncError && (
            <span className="missions-sync-error">{syncError} · </span>
          )}
          Team data in Firestore · ~1s save · labels in column menus
        </span>
      </div>

      <div className="missions-table-wrap">
        <table className="missions-table">
          <thead>
            <tr>
              <th scope="col" className="missions-col-mission">
                Mission
              </th>
              <th scope="col" className="missions-col-person">
                Person
              </th>
              <th scope="col" className="missions-col-status">
                Status
              </th>
              <th scope="col" className="missions-col-priority">
                Priority
              </th>
              <th scope="col" className="missions-col-date">
                End date
              </th>
              <th scope="col" className="missions-col-details">
                Details
              </th>
              <th scope="col" className="missions-col-actions">
                <span className="visually-hidden">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => (
              <tr key={row.id}>
                <td data-label="Mission">
                  <div className="missions-mission-cell">
                    <input
                      type="text"
                      className="missions-input missions-input--text"
                      value={row.mission}
                      onChange={(e) => updateRow(row.id, { mission: e.target.value })}
                      placeholder="Mission name"
                      aria-label="Mission"
                    />
                    <button
                      type="button"
                      className="missions-msg-btn"
                      onClick={(e) =>
                        setUpdatesUi({ rowId: row.id, rect: (e.currentTarget as HTMLButtonElement).getBoundingClientRect() })
                      }
                      aria-label={`Updates, ${row.updates.length} messages`}
                      title="Updates"
                    >
                      <svg className="missions-msg-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                        <path
                          fill="currentColor"
                          d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"
                        />
                      </svg>
                      <span className="missions-msg-count">{row.updates.length}</span>
                    </button>
                  </div>
                </td>
                <td data-label="Person">
                  <div className="missions-dd-cell missions-dd-cell--person">
                    <MondayMissionDropdown
                      instanceId={`d-${row.id}-person`}
                      field="person"
                      catalog={catalog}
                      value={row.person}
                      onPick={(person) => updateRow(row.id, { person })}
                      onApplyCatalog={applyCatalogFromInline}
                      ariaLabel="Person"
                      isOpen={dropdownOpen?.rowId === row.id && dropdownOpen?.field === 'person'}
                      onOpen={() => setDropdownOpen({ rowId: row.id, field: 'person' })}
                      onClose={() => setDropdownOpen(null)}
                    />
                  </div>
                </td>
                <td data-label="Status">
                  <div className="missions-dd-cell">
                    <MondayMissionDropdown
                      instanceId={`d-${row.id}-status`}
                      field="status"
                      catalog={catalog}
                      value={row.statusId}
                      onPick={(statusId) => updateRow(row.id, { statusId })}
                      onApplyCatalog={applyCatalogFromInline}
                      ariaLabel="Status"
                      isOpen={dropdownOpen?.rowId === row.id && dropdownOpen?.field === 'status'}
                      onOpen={() => setDropdownOpen({ rowId: row.id, field: 'status' })}
                      onClose={() => setDropdownOpen(null)}
                    />
                  </div>
                </td>
                <td data-label="Priority">
                  <div className="missions-dd-cell">
                    <MondayMissionDropdown
                      instanceId={`d-${row.id}-priority`}
                      field="priority"
                      catalog={catalog}
                      value={row.priorityId}
                      onPick={(priorityId) => updateRow(row.id, { priorityId })}
                      onApplyCatalog={applyCatalogFromInline}
                      ariaLabel="Priority"
                      isOpen={dropdownOpen?.rowId === row.id && dropdownOpen?.field === 'priority'}
                      onOpen={() => setDropdownOpen({ rowId: row.id, field: 'priority' })}
                      onClose={() => setDropdownOpen(null)}
                    />
                  </div>
                </td>
                <td data-label="End date">
                  <input
                    type="date"
                    className="missions-input missions-input--date"
                    value={row.endDate}
                    onChange={(e) => updateRow(row.id, { endDate: e.target.value })}
                    aria-label="End date"
                  />
                </td>
                <td data-label="Details">
                  <textarea
                    className="missions-input missions-input--details missions-details-teaser"
                    value={row.details}
                    onChange={(e) => updateRow(row.id, { details: e.target.value })}
                    onClick={() => setDetailsRowId(row.id)}
                    placeholder="Notes — tap for full editor"
                    rows={2}
                    aria-label="Details"
                  />
                </td>
                <td className="missions-actions-cell">
                  {viewMode === 'archived' ? (
                    <div className="missions-actions-inline">
                      <button
                        type="button"
                        className="missions-btn missions-btn--soft missions-actions-restore"
                        onClick={() => restoreMission(row.id)}
                      >
                        Restore
                      </button>
                      <button
                        type="button"
                        className="missions-btn missions-btn--ghost"
                        onClick={() => setPendingRemove({ id: row.id, context: 'archived' })}
                        aria-label="Delete permanently"
                        title="Delete permanently"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="missions-btn missions-btn--ghost"
                      onClick={() => setPendingRemove({ id: row.id, context: 'active' })}
                      aria-label="Remove mission"
                      title="Remove"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="missions-cards">
        {displayRows.map((row) => (
          <article key={row.id} className="missions-card">
            <div className="missions-card-header missions-card-header--mesh">
              <div className="missions-card-title-row">
                <label className="missions-field-label" htmlFor={`m-mission-${row.id}`}>
                  Mission
                </label>
                <div className="missions-card-actions">
                  {viewMode === 'archived' ? (
                    <>
                      <button
                        type="button"
                        className="missions-btn missions-btn--soft missions-card-remove"
                        onClick={() => restoreMission(row.id)}
                      >
                        Restore
                      </button>
                      <button
                        type="button"
                        className="missions-btn missions-btn--ghost missions-card-remove"
                        onClick={() => setPendingRemove({ id: row.id, context: 'archived' })}
                        aria-label="Delete permanently"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="missions-btn missions-btn--ghost missions-card-remove"
                      onClick={() => setPendingRemove({ id: row.id, context: 'active' })}
                      aria-label="Remove mission"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <div className="missions-mission-cell missions-mission-cell--card">
                <input
                  id={`m-mission-${row.id}`}
                  type="text"
                  className="missions-input missions-input--text missions-card-title-input"
                  value={row.mission}
                  onChange={(e) => updateRow(row.id, { mission: e.target.value })}
                  placeholder="Mission name"
                />
                <button
                  type="button"
                  className="missions-msg-btn"
                  onClick={(e) =>
                    setUpdatesUi({ rowId: row.id, rect: (e.currentTarget as HTMLButtonElement).getBoundingClientRect() })
                  }
                  aria-label={`Updates, ${row.updates.length} messages`}
                >
                  <svg className="missions-msg-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                    <path
                      fill="currentColor"
                      d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"
                    />
                  </svg>
                  <span className="missions-msg-count">{row.updates.length}</span>
                </button>
              </div>
            </div>
            <div className="missions-card-chips missions-card-chips--dropdowns">
              <MondayMissionDropdown
                instanceId={`m-${row.id}-status`}
                field="status"
                catalog={catalog}
                value={row.statusId}
                onPick={(statusId) => updateRow(row.id, { statusId })}
                onApplyCatalog={applyCatalogFromInline}
                ariaLabel="Status"
                isOpen={dropdownOpen?.rowId === row.id && dropdownOpen?.field === 'status'}
                onOpen={() => setDropdownOpen({ rowId: row.id, field: 'status' })}
                onClose={() => setDropdownOpen(null)}
              />
              <MondayMissionDropdown
                instanceId={`m-${row.id}-priority`}
                field="priority"
                catalog={catalog}
                value={row.priorityId}
                onPick={(priorityId) => updateRow(row.id, { priorityId })}
                onApplyCatalog={applyCatalogFromInline}
                ariaLabel="Priority"
                isOpen={dropdownOpen?.rowId === row.id && dropdownOpen?.field === 'priority'}
                onOpen={() => setDropdownOpen({ rowId: row.id, field: 'priority' })}
                onClose={() => setDropdownOpen(null)}
              />
            </div>
            <div className="missions-card-grid">
              <div className="missions-card-field missions-card-field--person-narrow">
                <span className="missions-field-label">Person</span>
                <MondayMissionDropdown
                  instanceId={`m-${row.id}-person`}
                  field="person"
                  catalog={catalog}
                  value={row.person}
                  onPick={(person) => updateRow(row.id, { person })}
                  onApplyCatalog={applyCatalogFromInline}
                  ariaLabel="Person"
                  isOpen={dropdownOpen?.rowId === row.id && dropdownOpen?.field === 'person'}
                  onOpen={() => setDropdownOpen({ rowId: row.id, field: 'person' })}
                  onClose={() => setDropdownOpen(null)}
                />
              </div>
              <div className="missions-card-field">
                <label className="missions-field-label" htmlFor={`m-date-${row.id}`}>
                  End date
                </label>
                <input
                  id={`m-date-${row.id}`}
                  type="date"
                  className="missions-input missions-input--date"
                  value={row.endDate}
                  onChange={(e) => updateRow(row.id, { endDate: e.target.value })}
                />
              </div>
            </div>
            <div className="missions-card-field missions-card-field--full">
              <label className="missions-field-label" htmlFor={`m-details-${row.id}`}>
                Details
              </label>
              <textarea
                id={`m-details-${row.id}`}
                className="missions-input missions-input--details missions-details-teaser"
                value={row.details}
                onChange={(e) => updateRow(row.id, { details: e.target.value })}
                onClick={() => setDetailsRowId(row.id)}
                placeholder="Notes — tap for full editor"
                rows={3}
              />
            </div>
          </article>
        ))}
      </div>

      {updatesUi && (
        <UpdatesAnchoredPopover
          row={updatesRow}
          anchorRect={updatesUi.rect}
          onClose={() => setUpdatesUi(null)}
          onPost={postUpdate}
        />
      )}
      {detailsRow && (
        <DetailsExpandModal
          row={detailsRow}
          onClose={() => setDetailsRowId(null)}
          onSave={(details) => detailsRowId && updateRow(detailsRowId, { details })}
        />
      )}
      {pendingRemove && pendingRemoveRow && (
        <RemoveMissionModal
          row={pendingRemoveRow}
          variant={pendingRemove.context}
          onClose={() => setPendingRemove(null)}
          onArchive={() => archiveMission(pendingRemove.id)}
          onDeletePermanent={() => deleteMissionPermanent(pendingRemove.id)}
        />
      )}
    </div>
  );
};

export default MissionsBoard;
